import {CloseDropdowns} from './Event';
import {initializeSelectContainer, state} from './State';
import {Drawer} from "../../Drawer";
import {cloneTemplate} from '../../../Library/WebComponents';

/** @param {HTMLElement} selectContainer */
export const positionDropdown = (selectContainer) => {
    const dropdown = selectContainer.querySelector('.dropdown');

    dropdown.classList.remove('right-aligned');

    if (dropdown.getBoundingClientRect().right > document.documentElement.clientWidth) {
        dropdown.classList.add('right-aligned');
    }

    if (dropdown.getBoundingClientRect().bottom > window.innerHeight) {
        dropdown.classList.add('inverted');

        if (dropdown.getBoundingClientRect().top < 0) {

            const currentViewableOptionCount = parseInt(
                window.getComputedStyle(selectContainer).getPropertyValue('--viewable-option-count'),
                10,
            );

            if (currentViewableOptionCount === 2) {
                return;
            }

            selectContainer.style.setProperty('--viewable-option-count', (currentViewableOptionCount - 1).toString(10));

            dropdown.classList.remove('inverted');

            positionDropdown(selectContainer);
        }
    }
};

const drawers = new WeakMap();

/** @param {HTMLElement} selectContainer */
export const openDropdown = selectContainer => {
    selectContainer.setAttribute('tabindex', '-1');
    selectContainer.classList.add('active');

    initializeSelectContainer(selectContainer);

    if (selectContainer.dataset.searchable === 'true' || selectContainer.dataset.serverSearch === 'true') {
        selectContainer.querySelector('input[type=text]').focus();
    }

    positionDropdown(selectContainer);

    state.hasActiveDropdown = true;

    if (selectContainer.dataset.isDrawer === 'true') {
        const drawer = Drawer(selectContainer.querySelector('.dropdown'), {
            mobileOnlyMask: true,
        });

        drawers.set(selectContainer, drawer);
        drawer.open();
    }
};

/** @param {HTMLElement} selectContainer */
export const closeDropdown = selectContainer => {
    selectContainer.setAttribute('tabindex', '0');
    selectContainer.classList.remove('active');

    const dropdown = selectContainer.querySelector('.dropdown');

    if (dropdown.dataset.inverted === undefined) {
        dropdown.classList.remove('inverted');
    }

    selectContainer.style.removeProperty('--viewable-option-count');

    state.hasActiveDropdown = false;

    if (selectContainer.dataset.isDrawer === 'true') {
        drawers.get(selectContainer)?.close();
    }
};

export const deselectMultiOption = (option, selectContainer) => {
    const optionValue = option.dataset.value;
    const selectElement = selectContainer.querySelector('select');
    const selectedOptionsContainer = selectContainer.querySelector('.selection');
    const icon = option.querySelector('i');

    option.classList.remove('selected');
    icon.classList.remove('fa-check');
    icon.classList.add('fa-plus');

    selectElement.querySelector('option[value="' + optionValue + '"]').removeAttribute('selected');
    const selectedOptionSpan = selectedOptionsContainer.querySelector('span[data-value="' + optionValue + '"]');
    if (selectedOptionSpan) {
        selectedOptionSpan.parentNode.removeChild(selectedOptionSpan);
    }

    if (selectedOptionsContainer.querySelectorAll('span').length === 0) {
        selectContainer.classList.remove('has-selection');
    }
};

export const selectMultiOption = (option, selectContainer) => {
    const optionValue = option.dataset.value;
    const selectElement = selectContainer.querySelector('select');
    const optionHtmlElement = selectElement.querySelector(`option[value="${optionValue}"]`);

    if (optionHtmlElement.selected) {
        return;
    }

    const selectedOptionsContainer = selectContainer.querySelector('.selection');
    const icon = option.querySelector('i');

    option.classList.add('selected');
    icon.classList.add('fa-check');
    icon.classList.remove('fa-plus');

    optionHtmlElement.setAttribute('selected', 'selected');

    const selectedOptionFragment = cloneTemplate('#multi-select-selected-option');
    const spanElement = selectedOptionFragment.querySelector('span');

    spanElement.childNodes[0].textContent = option.textContent;
    spanElement.dataset.value = option.dataset.value;

    selectedOptionsContainer.appendChild(selectedOptionFragment);
    selectContainer.classList.add('has-selection');
};

export const selectSingleOption = (selectContainer, option, selectElement) => {
    const currentSelection = selectContainer.querySelector('li.selected');

    if (currentSelection) {
        currentSelection.classList.remove('selected');
    }

    selectContainer.querySelector('.selection').childNodes[0].textContent = option.textContent;
    option.classList.add('selected');
    const optionValue = option.dataset.value;
    selectElement.querySelectorAll('option').forEach(e => e.removeAttribute('selected'));
    selectElement.querySelector('option[value="' + optionValue + '"]').setAttribute('selected', 'selected');
    selectContainer.classList.add('has-selection');
    document.dispatchEvent(new CloseDropdowns(true));
};

/**
 *
 * @param {HTMLElement} selectContainer
 * @param {string:'up'|'down'} direction
 * @return ?HTMLElement
 */
export const navigate = (selectContainer, direction) => {
    const options = Array.from(selectContainer.querySelectorAll('.dropdown li'));
    const optionContainer = selectContainer.querySelector('.options');
    const focusedOption = optionContainer.querySelector('.focus') || optionContainer.querySelector(':hover');

    if (direction === 'down') {
        return navigateDown(options, focusedOption);
    }

    return navigateUp(options, focusedOption);
};

/**
 *
 * @param {Array<HTMLElement>} options
 * @param {null|HTMLElement} currentOption
 * @return ?HTMLElement
 */
const navigateUp = (options, currentOption) => {
    const predicate = currentOption !== null
        ? (option, index) => index < options.indexOf(currentOption) && !option.matches('.hidden, .disabled')
        : option => !option.matches('.hidden, .disabled')
    ;

    const nextOption = options.findLast(predicate);

    if (nextOption) {
        if (currentOption) {
            currentOption.classList.remove('focus');
        }
        nextOption.classList.add('focus');
    }

    return nextOption;
};

/**
 *
 * @param {Array<HTMLElement>} options
 * @param {null|HTMLElement} currentOption
 * @return ?HTMLElement
 */
const navigateDown = (options, currentOption = null) => {
    const predicate = currentOption !== null
        ? (option, index) => index > options.indexOf(currentOption) && !option.matches('.hidden, .disabled')
        : option => !option.matches('.hidden, .disabled')
    ;

    const nextOption = options.find(predicate);

    if (nextOption) {
        if (currentOption) {
            currentOption.classList.remove('focus');
        }
        nextOption.classList.add('focus');
    }

    return nextOption;
};

export const outOfVerticalBounds = (target, container) => {
    return target.getBoundingClientRect().bottom > container.getBoundingClientRect().bottom
        || target.getBoundingClientRect().top < container.getBoundingClientRect().top;
};
